import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../../Helpers/Handler";
import { formatDateTime, textCapitalize } from "../../../Helpers/Utils";
import { StatusPill } from "../../../Components/Needed";
import ListLayout from "../../../Components/List";
import { VERIFICATION_STATUSES } from "../shared/vehicleFleetConstants";
import BikeFormModal from "./BikeFormModal";
import useListParams from "../../../Helpers/useListParams";

// bikeList's validator only supports search/verificationStatus/status/
// sortBy/sort (no fuelType/transmissionType - Bike has neither), unlike
// Car's list filters.
const INITIAL_FILTERS = {
    search: "", searchKey: "registrationNo", count: 10,
    verificationStatus: "", status: "",
    sortBy: "createdAt", sort: false,
};

const SEARCH_KEYS = [{ key: "registrationNo", value: "Registration no." }];

const FILTER_ITEMS = {
    dropdown: [
        { key: "verificationStatus", name: "Verification", options: [{ key: "", name: "All" }, ...VERIFICATION_STATUSES.map((v) => ({ key: v, name: textCapitalize(v) }))] },
        { key: "status", name: "Status", options: [{ key: "", name: "All" }, { key: true, name: "Active" }, { key: false, name: "Inactive" }] },
    ],
    sort: [
        { key: "registrationNo", name: "Registration no." },
        { key: "createdAt", name: "Created" },
    ],
};

const BikeList = () => {
    const navigate = useNavigate();
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/staff/bike/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.verificationStatus ? { verificationStatus: filters.verificationStatus } : {}),
                ...(filters.status !== "" ? { status: filters.status } : {}),
                sortBy: filters.sortBy,
                sort: filters.sort,
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.verificationStatus, filters.status, filters.sortBy, filters.sort]);

    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const columns = [
        { key: "registrationNo", name: "Registration no.", mandatory: true },
        { key: "color", name: "Color", render: (row) => row.color || "-" },
        { key: "verificationStatus", name: "Verification", render: (row) => <span className="text-capitalize">{row.verificationStatus}</span> },
        { key: "status", name: "Status", render: (row) => <StatusPill active={row.status} /> },
        { key: "createdAt", name: "Created", render: (row) => formatDateTime(row.createdAt) },
    ];

    return (
        <>
            <ListLayout
                title="Bikes"
                description="The bike fleet - registration, device/odometer tracking, pricing, verification and documents."
                data={data}
                loading={loading}
                error={error}
                onRetry={load}
                onRefresh={load}
                table={columns}
                page={page}
                setPageNo={setPage}
                pageSize={filters.count}
                onPageSizeChange={(size) => onFilterChange("count", size)}
                filters={filters}
                initialFilters={INITIAL_FILTERS}
                filterItems={FILTER_ITEMS}
                onFilterChange={onFilterChange}
                resetFilter={resetFilter}
                searchKeys={SEARCH_KEYS}
                searchPlaceholder="Search by registration no..."
                onRowClick={(row) => navigate(`/vehicle-fleet/bikes/${row.bikeId}`)}
                addList={() => setShowAddModal(true)}
            />

            {showAddModal ? (
                <BikeFormModal
                    mode="create"
                    onClose={() => setShowAddModal(false)}
                    onSaved={() => { setShowAddModal(false); load(); }}
                />
            ) : ""}
        </>
    );
};

export default BikeList;
